'use client';

import { useState, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { AppSelect } from '@/components/ui/AppSelect';
import { useTranslations } from 'next-intl';

export interface FilterValue {
  search?: string;
  /** Slug of the active category. Owned by the page (the category chips link to category pages). */
  category?: string;
  sort?: string;
  page?: number;
  limit?: number;
  [key: string]: any;
}

interface FilterBarProps {
  /** Plain-text topic pills (e.g. FAQ topics). Project/blog categories use <CategoryChips> instead. */
  categories?: string[];
  sortOptions: { value: string; label: string }[];
  /** Accepted for call-site compatibility; the component localizes its own labels. */
  locale?: string;
  value: FilterValue;
  onChange: (filters: FilterValue) => void;
  showFeaturedToggle?: boolean;
  featured?: boolean;
  searchPlaceholder?: string;
  total?: number;
  className?: string;
}

const control = 'h-10 rounded-lg border text-sm transition-colors duration-180';
const toggleBase = `${control} shrink-0 px-3 font-medium cursor-pointer`;
const toggleOn = 'bg-primary-500 text-neutral-900 border-primary-500';
const toggleOff = 'border-(--border) bg-(--bg) text-(--muted) hover:border-primary-500/50 hover:text-(--text)';

/**
 * Search + sort + page size (+ optional "featured" toggle). Category selection is not handled here:
 * it lives in <CategoryChips>, whose links point at real category pages.
 */
export function FilterBar({
  categories = [],
  sortOptions,
  value,
  onChange,
  showFeaturedToggle = false,
  featured = false,
  searchPlaceholder,
  total,
  className = '',
}: FilterBarProps) {
  const t = useTranslations('FilterBar');
  const [searchInput, setSearchInput] = useState(value.search ?? '');
  const debouncedSearch = useDebounce(searchInput, 400);

  const perPageOptions = [6, 9, 12, 24, 48].map(n => ({
    value: String(n),
    label: `${n} / ${t('page')}`,
  }));

  useEffect(() => {
    if (debouncedSearch !== (value.search ?? '')) {
      onChange({ ...value, search: debouncedSearch || undefined, page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleCategoryChange = useCallback(
    (cat: string) => onChange({ ...value, category: cat, page: 1 }),
    [value, onChange]
  );
  const handleSortChange = useCallback(
    (sort: string) => onChange({ ...value, sort: sort || undefined, page: 1 }),
    [value, onChange]
  );
  const handleFeaturedToggle = useCallback(
    () => onChange({ ...value, featured: !featured, page: 1 }),
    [value, featured, onChange]
  );
  const handleLimitChange = useCallback(
    (limit: string) => onChange({ ...value, limit: Number(limit), page: 1 }),
    [value, onChange]
  );
  const handleClear = useCallback(() => {
    setSearchInput('');
    onChange({ ...value, search: undefined, featured: undefined, page: 1 });
  }, [value, onChange]);

  const filtering = Boolean(value.search || featured);

  return (
    <div className={`px-4 sm:px-6 pb-8 ${className}`}>
      <div className="max-w-6xl mx-auto rounded-2xl border border-(--border) bg-(--surface) p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-3">

          {/* Search */}
          <div className="relative flex-1 min-w-56">
            <Search className="absolute inset-s-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--muted)" aria-hidden="true" />
            <input
              type="search"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder={searchPlaceholder ?? t('search')}
              className={`${control} w-full ps-9 pe-3 border-(--border) bg-(--bg) text-(--text) placeholder:text-(--muted) focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30`}
              aria-label={t('search')}
            />
          </div>

          {/* Topic pills (string categories, e.g. FAQ) */}
          {categories.length > 0 && (
            <div className="flex max-w-full items-center gap-2 overflow-x-auto">
              {['all', ...categories].map(cat => {
                const isActive = cat === 'all' ? !value.category || value.category === 'all' : value.category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleCategoryChange(cat)}
                    aria-pressed={isActive}
                    className={`${toggleBase} ${isActive ? toggleOn : toggleOff}`}
                  >
                    {cat === 'all' ? t('all') : cat}
                  </button>
                );
              })}
            </div>
          )}

          {/* Featured toggle */}
          {showFeaturedToggle && (
            <button
              type="button"
              onClick={handleFeaturedToggle}
              aria-pressed={featured}
              className={`${toggleBase} ${featured ? toggleOn : toggleOff}`}
            >
              ★ {t('featured')}
            </button>
          )}

          {/* Sort */}
          {sortOptions.length > 0 && (
            <AppSelect
              value={value.sort ?? ''}
              onChange={handleSortChange}
              options={sortOptions}
              placeholder={t('sortBy')}
              aria-label={t('sortBy')}
              className="shrink-0 min-w-44 max-sm:flex-1"
            />
          )}

          {/* Per-page */}
          <AppSelect
            value={String(value.limit ?? 12)}
            onChange={handleLimitChange}
            options={perPageOptions}
            aria-label={t('perPage')}
            className="shrink-0 min-w-32 max-sm:flex-1"
          />
        </div>

        {/* Result count + reset */}
        {(total !== undefined || filtering) && (
          <div className="mt-3 flex items-center justify-between gap-3 border-t border-(--border) pt-3 text-xs text-(--muted)">
            <span aria-live="polite">{total !== undefined ? t('results', { count: total }) : ''}</span>
            {filtering && (
              <button
                type="button"
                onClick={handleClear}
                className="inline-flex items-center gap-1 font-medium text-primary-500 hover:text-primary-400 transition-colors cursor-pointer"
              >
                <X className="w-3.5 h-3.5" aria-hidden="true" />
                {t('clear')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FilterBar;
